import { Box, BoxProps, Card, Flex, Heading, Link, List, ListItem, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { Markdown } from '~/components/markdown';
import { ProgramContent } from '~/features/program/models/program-content';

export type SalariesSectionProps = NonNullable<StaticSalariesSectionProps>;

export function SalariesSection({ relatedProfession }: SalariesSectionProps) {
  if (!relatedProfession) return null;
  return (
    <Box as='section'>
      <ContentContainer>
        <Heading
          as='h3'
          mb={4}
          size={{
            base: 'heading-7',
            md: 'heading-6',
          }}
        >
          Skills that demand high salaries
        </Heading>
        <Card
          bgGradient='linear(180deg, #0E3FB2 0%, #368DFF 100%)'
          borderRadius='8px'
          color='white'
          display='flex'
          flexDirection='column'
          gap={4}
          padding={{
            base: 4,
            md: 8,
          }}
        >
          <Markdown markdownText={relatedProfession.industryInsight} />
          <Card color='black' display='flex' flexDirection='column' gap={4} padding='16px 24px'>
            <Flex alignItems='center' flexWrap='wrap' justifyContent='space-between'>
              <Heading as='p' size='heading-7'>
                {relatedProfession.name}
              </Heading>
              <Box
                as='small'
                color='supportGray.60'
                display='block'
                flexShrink={1}
                fontSize='xs'
                lineHeight='xs'
                textAlign='center'
              >
                Salary info from{' '}
                <Link
                  color='supportBlue.70'
                  fontSize='xs'
                  fontWeight='400'
                  href='https://www.talent.com/'
                  lineHeight='xs'
                  _hover={{
                    color: 'supportBlue.70',
                  }}
                  _visited={{
                    color: 'supportBlue.70',
                  }}
                  isExternal
                >
                  Talent.com
                </Link>
              </Box>
            </Flex>
            <Flex flexDirection='column' gap='14px' mt='30px'>
              <Box
                bgGradient='linear(90deg, #FFC175 16.81%, #FB5709 50%, #175CFF 83.41%);'
                borderRadius='8px'
                flex='0 0 4px'
              />
              <List display='flex' gap={2} justifyContent='space-between' position='relative'>
                <SalaryItem
                  bgColor='supportOrange.10'
                  borderColor='supportOrange.40'
                  dotColor='#FFC175'
                  label='Low'
                  value={relatedProfession.salaryLow}
                />
                <SalaryItem
                  bgColor='#FFECE4'
                  borderColor='#FB570926'
                  dotColor='#FB5709'
                  label='Average'
                  value={relatedProfession.salaryAverage}
                />
                <SalaryItem
                  bgColor='supportBlue.10'
                  borderColor='supportBlue.40'
                  dotColor='#175CFF'
                  label='High'
                  value={relatedProfession.salaryHigh}
                />
              </List>
            </Flex>
          </Card>
        </Card>
      </ContentContainer>
    </Box>
  );
}

function SalaryItem({
  label,
  value,
  dotColor,
  ...boxProps
}: { label: string; value: string; dotColor?: string } & BoxProps) {
  return (
    <ListItem display='flex' flex='1' justifyContent='center'>
      <Box
        border='1px solid'
        borderRadius='4px'
        padding={1}
        position='relative'
        textAlign='center'
        _after={{
          content: '"⬤"',
          display: 'block',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -190%)',
          fontSize: {
            base: '6px',
            md: '8px',
          },
          fontWeight: '600',
          color: dotColor,
        }}
        flex={{
          md: '0 1 100px',
        }}
        fontSize={{
          base: 'sm',
          md: 'md',
        }}
        {...boxProps}
      >
        <Text
          fontWeight='600'
          left='50%'
          position='absolute'
          top='50%'
          fontSize={{
            base: 'sm',
            md: 'md',
          }}
          transform={{
            base: 'translate(-50%, -330%)',
            md: 'translate(-50%, -300%)',
          }}
        >
          {label}
        </Text>
        {value}
      </Box>
    </ListItem>
  );
}

export function transformStaticSalariesSectionProps({ programContent }: { programContent: ProgramContent }) {
  if (!programContent.relatedProfession) {
    return null;
  }

  return {
    relatedProfession: programContent.relatedProfession,
  };
}

export type StaticSalariesSectionProps = ReturnType<typeof transformStaticSalariesSectionProps>;
